import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  Botao,
  Breadcrumb,
  Cabecalho,
  Cores,
  CpfInputUnform,
  DataInputUnform,
  ErroLayoutContainer,
  Filtro,
  IconeCopiar,
  IconeDuplicar,
  IconeEditar,
  IconeRevelarSenha,
  InputUnform,
  SelectUnform,
  Tooltip
} from 'src/componentes'
import { FuncoesDataHora, FuncoesMoeda } from 'src/componentes/funcoes'
import {
  Tabela,
  TabelaDados,
  TabelaRef,
  TabelaResposta
} from 'src/componentes/tabela'
import { RotasCondicaoEspecial } from 'src/rotas/academico'
import { Api } from 'src/servicos'
import {
  CorStatusCondicaoEspecial,
  StatusCondicaoEspecial,
  StatusCondicaoEspecialPorExtenso,
  TipoOrdenacao
} from 'src/tipos'

import { copiarLinkDoAluno } from './link-aluno'
import { motivoEdicaoBloqueada } from './motivo-edicao-bloqueada'
import {
  BadgeSemStatus,
  BadgeStatus,
  Campo,
  Container,
  ContainerAcoes,
  Dados,
  Form,
  IconeAcao
} from './styles'

interface DadosPesquisaCondicaoEspecial {
  nomeContato?: string
  cpf?: string
  numeroProtocolo?: string
  status?: string[]
  dataAberturaInicio?: string
  dataAberturaFim?: string
}

const renderizarBadgeStatus = (status: string, expirada?: boolean) => {
  const chave = status as keyof typeof StatusCondicaoEspecial
  const cor = CorStatusCondicaoEspecial[chave]
  const texto = StatusCondicaoEspecialPorExtenso[chave] ?? status

  if (!cor) {
    return <BadgeSemStatus>{texto}</BadgeSemStatus>
  }

  const corExibida =
    expirada && status !== StatusCondicaoEspecial.Expirada
      ? CorStatusCondicaoEspecial.Expirada
      : cor

  return (
    <BadgeStatus fundo={corExibida.fundo} corTexto={corExibida.texto}>
      {texto}
      {expirada && status !== StatusCondicaoEspecial.Expirada
        ? ' (vencida)'
        : ''}
    </BadgeStatus>
  )
}

export const PaginaCondicaoEspecial: React.FC = () => {
  const tabelaRef = useRef<TabelaRef>()
  const [
    dadosPesquisa,
    definirDadosPesquisa
  ] = useState<DadosPesquisaCondicaoEspecial>(null)
  const [paginas, definirPaginas] = useState(null)
  const history = useHistory()

  const acaoPesquisa = (dados: DadosPesquisaCondicaoEspecial) => {
    if (dados !== null && paginas !== 1) {
      definirPaginas(1)
    }

    definirDadosPesquisa(dados)
  }

  const obterDados = async (
    numeroPaginas: number,
    quantidadePorPagina: number,
    chaveOrdenacao?: string,
    ordem?: TipoOrdenacao
  ): Promise<TabelaResposta | null> => {
    if (paginas !== null) {
      numeroPaginas = paginas
    }

    const dados = await Api.ListarCondicoesEspeciais(
      {
        nomeContato: dadosPesquisa?.nomeContato,
        cpf: dadosPesquisa?.cpf,
        numeroProtocolo: dadosPesquisa?.numeroProtocolo,
        status: dadosPesquisa?.status,
        dataAberturaInicio: dadosPesquisa?.dataAberturaInicio,
        dataAberturaFim: dadosPesquisa?.dataAberturaFim
      },
      numeroPaginas,
      quantidadePorPagina,
      chaveOrdenacao,
      ordem
    )

    if (!dados) return null

    definirPaginas(null)

    return {
      Dados: dados.itens,
      Paginacao: {
        Pagina: numeroPaginas,
        TotalRegistros: dados.quantidade
      }
    } as TabelaResposta
  }

  const abrirDetalhe = (id: string) =>
    history.push(`${RotasCondicaoEspecial.Detalhe}/${id}`)

  const abrirEdicao = (id: string) =>
    history.push(`${RotasCondicaoEspecial.Edicao}/${id}`)

  const abrirDuplicacao = (id: string) =>
    history.push(`${RotasCondicaoEspecial.Duplicacao}/${id}`)

  useEffect(() => {
    if (dadosPesquisa === null) return
    tabelaRef.current?.IrParaPagina()
    tabelaRef.current?.CarregarDados()
  }, [dadosPesquisa])

  return (
    <>
      <Cabecalho>
        <Breadcrumb
          titulo="Condições especiais"
          atalhos={[{ texto: 'Financeiro' }, { texto: 'Condição especial' }]}
        />
        <Botao
          type="button"
          id="btn-incluir-condicao-especial"
          texto="Incluir"
          tema="Secundario"
          onClick={() => history.push(RotasCondicaoEspecial.Cadastro)}
        />
      </Cabecalho>
      <Filtro>
        <Form acaoSucesso={acaoPesquisa}>
          <Container>
            <Campo mr>
              <InputUnform
                type="text"
                name="nomeContato"
                label="Nome do aluno ou contato"
                maxLength={200}
              />
            </Campo>
            <Campo mr>
              <CpfInputUnform type="text" name="cpf" label="CPF" />
            </Campo>
            <Campo>
              <InputUnform
                type="text"
                name="numeroProtocolo"
                label="Nº Protocolo"
                maxLength={50}
              />
            </Campo>
          </Container>
          <Container>
            <Campo mr>
              <SelectUnform
                id="slct_status"
                name="status"
                label="Status"
                iconeFechar
                multiplo
                opcoes={Object.keys(StatusCondicaoEspecial).map(chave => ({
                  id: chave,
                  texto:
                    StatusCondicaoEspecialPorExtenso[
                    chave as keyof typeof StatusCondicaoEspecial
                    ]
                }))}
              />
            </Campo>
            <Campo mr>
              <DataInputUnform
                name="dataAberturaInicio"
                label="Data Abertura Início"
              />
            </Campo>
            <Campo>
              <DataInputUnform
                name="dataAberturaFim"
                label="Data Abertura Fim"
              />
            </Campo>
          </Container>
          <Botao
            type="submit"
            id="btn-aplicar-filtro"
            texto="Aplicar filtro"
            tema="Link"
          />
        </Form>
      </Filtro>
      <Dados>
        <Tabela
          ref={tabelaRef}
          Colunas={[
            {
              Chave: 'numeroProtocolo',
              RenderizarCabecalho: () => (
                <div style={{ width: 95 }}>Protocolo</div>
              )
            },
            {
              Chave: 'status',
              Renderizar: (linha: TabelaDados) =>
                renderizarBadgeStatus(linha.status, linha.expirada),
              RenderizarCabecalho: () => (
                <div style={{ width: 120 }}>Status</div>
              )
            },
            {
              Chave: 'nomeContato',
              Renderizar: (linha: TabelaDados) => (
                <>{linha.nomeContato ?? '—'}</>
              ),
              RenderizarCabecalho: () => (
                <div style={{ width: 130 }}>Aluno / Contato</div>
              )
            },
            {
              Chave: 'cpf',
              Renderizar: (linha: TabelaDados) => <>{linha.cpf ?? '—'}</>,
              RenderizarCabecalho: () => <div style={{ width: 100 }}>CPF</div>
            },
            {
              Chave: 'curso',
              Renderizar: (linha: TabelaDados) => <>{linha.curso ?? '—'}</>,
              RenderizarCabecalho: () => <div style={{ width: 120 }}>Curso</div>
            },
            {
              Chave: 'turma',
              Renderizar: (linha: TabelaDados) => <>{linha.turma ?? '—'}</>,
              RenderizarCabecalho: () => <div style={{ width: 90 }}>Turma</div>
            },
            {
              Chave: 'valorNegociado',
              Renderizar: (linha: TabelaDados) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                  {linha.valorNegociado !== null &&
                    linha.valorNegociado !== undefined
                    ? FuncoesMoeda.formatarMoeda(linha.valorNegociado)
                    : '—'}
                </span>
              ),
              RenderizarCabecalho: () => (
                <div style={{ width: 100 }}>Valor negociado</div>
              )
            },
            {
              Chave: 'consultor',
              Renderizar: (linha: TabelaDados) => <>{linha.consultor ?? '—'}</>,
              RenderizarCabecalho: () => (
                <div style={{ width: 110 }}>Consultor</div>
              )
            },
            {
              Chave: 'dataAbertura',
              Renderizar: (linha: TabelaDados) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                  {FuncoesDataHora.dataStringFormatadaComBarras(
                    linha.dataAbertura
                  )}
                </span>
              ),
              RenderizarCabecalho: () => (
                <div style={{ width: 90 }}>Abertura</div>
              )
            },
            {
              Chave: 'acoes',
              RenderizarCabecalho: () => <div style={{ width: 120 }}>Ações</div>,
              Renderizar: (linha: TabelaDados) => {
                const DETALHE_ID = `CONDICAO_ESPECIAL_DETALHE/${linha.id}`
                const EDITAR_ID = `CONDICAO_ESPECIAL_EDITAR/${linha.id}`
                const LINK_ID = `CONDICAO_ESPECIAL_LINK/${linha.id}`
                const DUPLICAR_ID = `CONDICAO_ESPECIAL_DUPLICAR/${linha.id}`

                // PodeEditar() do domínio, vindo da listagem: inclui a expirada
                // que nenhum aluno assumiu, que reabre ao estender a validade.
                const podeEditar = Boolean(linha.podeEditar)

                return (
                  <ContainerAcoes>
                    <IconeAcao
                      onClick={() => abrirDetalhe(linha.id)}
                      onContextMenu={() => {
                        const rota = window.open(
                          `${RotasCondicaoEspecial.Detalhe}/${linha.id}`
                        )
                        rota?.focus()
                      }}
                      data-tip="Abrir detalhe da condição especial"
                      data-for={DETALHE_ID}
                    >
                      {IconeRevelarSenha}
                      <Tooltip
                        id={DETALHE_ID}
                        textColor={Cores.PRETO}
                        backgroundColor={Cores.CINZA_1_CLARO}
                        place="right"
                      />
                    </IconeAcao>
                    <IconeAcao
                      onClick={() => podeEditar && abrirEdicao(linha.id)}
                      disabled={!podeEditar}
                      data-tip={
                        podeEditar
                          ? 'Editar condição especial'
                          : motivoEdicaoBloqueada(linha.status)
                      }
                      data-for={EDITAR_ID}
                    >
                      {IconeEditar}
                      <Tooltip
                        id={EDITAR_ID}
                        textColor={Cores.PRETO}
                        backgroundColor={Cores.CINZA_1_CLARO}
                        place="right"
                      />
                    </IconeAcao>
                    <IconeAcao
                      onClick={() => copiarLinkDoAluno(linha.id)}
                      data-tip="Copiar link do aluno"
                      data-for={LINK_ID}
                    >
                      {IconeCopiar}
                      <Tooltip
                        id={LINK_ID}
                        textColor={Cores.PRETO}
                        backgroundColor={Cores.CINZA_1_CLARO}
                        place="right"
                      />
                    </IconeAcao>
                    {/*
                      Sem trava de status: duplicar é justamente o que se faz com uma
                      condição cancelada, vencida ou já concluída. Abre o formulário
                      pré-preenchido — nada é criado até o consultor salvar.
                    */}
                    <IconeAcao
                      onClick={() => abrirDuplicacao(linha.id)}
                      data-tip="Duplicar condição especial"
                      data-for={DUPLICAR_ID}
                    >
                      {IconeDuplicar}
                      <Tooltip
                        id={DUPLICAR_ID}
                        textColor={Cores.PRETO}
                        backgroundColor={Cores.CINZA_1_CLARO}
                        place="right"
                      />
                    </IconeAcao>
                  </ContainerAcoes>
                )
              }
            }
          ]}
          ObterDados={obterDados}
          ComPaginacao
        />
      </Dados>
    </>
  )
}

export default (): JSX.Element => (
  <ErroLayoutContainer.Provider>
    <PaginaCondicaoEspecial />
  </ErroLayoutContainer.Provider>
)

export { default as PaginaCadastroCondicaoEspecial } from './cadastro'
export { default as PaginaDetalheCondicaoEspecial } from './detalhe'
